import { useState, useEffect, useCallback } from "react";
import { API_BASE } from "../config/api";

export default function GalleryPage() {
  const [events, setEvents]   = useState([]);
  const [loading, setLoading] = useState(true);
  const [lightbox, setLightbox] = useState(null); // { images, index }

  useEffect(() => {
    fetch(`${API_BASE}/gallery`)
      .then(r => r.json())
      .then(data => {
        if (!Array.isArray(data)) { setEvents([]); return; }
        // Group images by title+description into events
        const map = {};
        data.forEach(item => {
          const key = `${item.title}||${item.description || ""}`;
          if (!map[key]) {
            map[key] = { title: item.title, description: item.description || "", images: [], date: item.created_at };
          }
          map[key].images.push({ id: item.id, url: item.image_url, title: item.title });
        });
        setEvents(Object.values(map));
      })
      .finally(() => setLoading(false));
  }, []);

  const openLightbox = (images, index) => setLightbox({ images, index });
  const closeLightbox = () => setLightbox(null);

  const prev = useCallback(() => {
    setLightbox(lb => ({ ...lb, index: (lb.index - 1 + lb.images.length) % lb.images.length }));
  }, []);

  const next = useCallback(() => {
    setLightbox(lb => ({ ...lb, index: (lb.index + 1) % lb.images.length }));
  }, []);

  // Keyboard navigation
  useEffect(() => {
    if (!lightbox) return;
    const handler = e => {
      if (e.key === "ArrowLeft")  prev();
      if (e.key === "ArrowRight") next();
      if (e.key === "Escape")     closeLightbox();
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [lightbox, prev, next]);

  return (
    <div className="page">
      <header className="page__hero">
        <h1>Gallery</h1>
        <p>Moments from Mokorotlo College of Creative Technology</p>
      </header>

      <section className="section">
        <div className="section__inner">
          {loading ? (
            <p style={{ textAlign: "center", color: "var(--text-muted)" }}>Loading…</p>
          ) : events.length === 0 ? (
            <p style={{ textAlign: "center", color: "var(--text-muted)" }}>No gallery items yet.</p>
          ) : (
            <div className="gallery-events">
              {events.map((event, ei) => (
                <div key={ei} className="gallery-event">
                  {/* Event header */}
                  <div className="gallery-event__header">
                    <h2 className="gallery-event__title">{event.title}</h2>
                    {event.description && (
                      <p className="gallery-event__desc">{event.description}</p>
                    )}
                    <span className="gallery-event__count">{event.images.length} photo{event.images.length !== 1 ? "s" : ""}</span>
                  </div>

                  {/* Photo grid */}
                  <div className="gallery-event__grid">
                    {event.images.map((img, ii) => (
                      <button
                        key={img.id}
                        className="gallery-photo"
                        onClick={() => openLightbox(event.images, ii)}
                        aria-label={`View photo ${ii + 1} of ${event.title}`}
                      >
                        <img src={img.url} alt={`${event.title} ${ii + 1}`} className="gallery-photo__img" loading="lazy" />
                        <div className="gallery-photo__overlay">
                          <span className="gallery-photo__icon">🔍</span>
                          <span className="gallery-photo__text">View</span>
                        </div>
                      </button>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* Lightbox */}
      {lightbox && (
        <div className="lightbox" onClick={closeLightbox} role="dialog" aria-modal="true">
          <div className="lightbox__inner" onClick={e => e.stopPropagation()}>
            <button className="lightbox__close" onClick={closeLightbox} aria-label="Close">✕</button>

            {/* Prev / Next */}
            {lightbox.images.length > 1 && (
              <>
                <button className="lightbox__nav lightbox__nav--prev" onClick={prev} aria-label="Previous">‹</button>
                <button className="lightbox__nav lightbox__nav--next" onClick={next} aria-label="Next">›</button>
              </>
            )}

            <img
              src={lightbox.images[lightbox.index].url}
              alt={lightbox.images[lightbox.index].title}
              className="lightbox__img"
            />

            <div className="lightbox__caption">
              <strong>{lightbox.images[lightbox.index].title}</strong>
              {lightbox.images.length > 1 && (
                <span style={{ color: "var(--text-muted)", fontSize: "0.85rem", marginLeft: 10 }}>
                  {lightbox.index + 1} / {lightbox.images.length}
                </span>
              )}
            </div>

            {/* Thumbnail strip */}
            {lightbox.images.length > 1 && (
              <div className="lightbox__thumbs">
                {lightbox.images.map((img, i) => (
                  <button
                    key={img.id}
                    className={`lightbox__thumb ${i === lightbox.index ? "lightbox__thumb--active" : ""}`}
                    onClick={() => setLightbox(lb => ({ ...lb, index: i }))}
                    aria-label={`Go to photo ${i + 1}`}
                  >
                    <img src={img.url} alt={`thumb-${i}`} />
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
